/*
 * search-augment — adds variant/shade-name matches to the /search RESULTS page.
 *
 * Shopify's native storefront search can mix exact variant shade hits with fuzzy matches
 * from broad product fields. The predictive API exposes variant-focused candidates, capped
 * at 10 results, so the theme can identify exact shade and SKU searches.
 *
 * This fetches sections/search-augment.liquid from the PREDICTIVE endpoint via the Section
 * Rendering API — one request returns every shade/variant match already rendered as a
 * card-product-v2 cell, server-side TARGETED to the searched shade (matched variant's
 * image/price/swatch + a ?variant=<id> link). When at least one exact normalized shade or
 * SKU match exists, the grid switches to strict shade mode: fuzzy native and predictive
 * matches are removed, exact cards are de-duplicated, and ordinary searches stay unchanged.
 *
 * Scope guards — augmentation deliberately does NOT run when the results are paginated
 * past page 1 (the same augmented set would repeat on every page), sorted, or facet-
 * filtered (appended cards can't honor sort order or active filters). facets.js replaces
 * #ProductGridContainer's innerHTML on every AJAX filter/sort change, so we re-run on its
 * 'collection:rerendered' event: cards come back from the cached fetch when the shopper
 * clears filters/sorting, and stay away while filters are active.
 */
(function () {
  'use strict';

  var fetchedCells = null; // cache — one predictive request per page load, reused across rerenders
  var FETCH_TIMEOUT_MS = 5000; // abort a hung suggest request — the grid stays CSS-hidden until finish()

  function shouldSkip() {
    var params = new URLSearchParams(window.location.search);
    if ((parseInt(params.get('page'), 10) || 1) > 1) return true;
    if (params.get('sort_by')) return true;
    var filtered = false;
    params.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0) filtered = true;
    });
    return filtered;
  }

  function augment() {
    var grid = document.getElementById('ProductsList');
    var gridWrap = document.getElementById('ProductGridContainer');
    if (!grid || !gridWrap) return; // only present on the search results page

    gridWrap.setAttribute('aria-busy', 'true');
    var q = (gridWrap.getAttribute('data-search-terms') || '').trim();
    if (!q || shouldSkip()) {
      finish(gridWrap);
      return;
    }

    if (fetchedCells) {
      try {
        inject(grid, gridWrap, fetchedCells);
      } finally {
        finish(gridWrap);
      }
      return;
    }

    // main-search.liquid renders this exact URL plus a matching <link rel="preload" as="fetch">,
    // so the response is usually already in flight before this deferred script runs.
    var url = gridWrap.getAttribute('data-augment-url');
    if (!url) {
      // Same route source as search.js, so locale-prefixed shops keep working.
      var route =
        (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.predictive_search_url) || '/search/suggest';
      // fields EXCLUDE title on purpose: native /search already shows title matches;
      // spending the 10-slot predictive cap on them crowds out novel shade matches.
      url =
        route + '?q=' + encodeURIComponent(q) +
        '&resources%5Btype%5D=product' +
        '&resources%5Boptions%5D%5Bfields%5D=variants.title,variants.sku' +
        '&resources%5Blimit%5D=10' +
        '&section_id=search-augment';
    }

    var controller = window.AbortController ? new AbortController() : null;
    var timer = controller && setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS);

    fetch(url, controller ? { signal: controller.signal } : {})
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        fetchedCells = Array.prototype.slice.call(doc.querySelectorAll('[data-augment-handle]'));
        inject(grid, gridWrap, fetchedCells);
      })
      .catch(function () {})
      .finally(function () {
        if (timer) clearTimeout(timer);
        finish(gridWrap);
      });
  }

  function nativeHandles(grid) {
    var map = Object.create(null);
    grid.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      var m = (a.getAttribute('href') || '').match(/\/products\/([^\/?#]+)/i);
      if (!m) return;
      try {
        map[decodeURIComponent(m[1]).toLowerCase()] = true;
      } catch (error) {
        map[m[1].toLowerCase()] = true;
      }
    });
    return map;
  }

  function inject(grid, gridWrap, cells) {
    var exactCells = cells.filter(isExactShadeCell);
    if (!exactCells.length) return;

    Array.prototype.slice.call(grid.children).forEach(function (cell) {
      if (cell.classList.contains('search-augment__divider') ||
          (cell.classList.contains('card') && !isExactShadeCell(cell))) {
        cell.remove();
      }
    });

    var native = nativeHandles(grid);
    var frag = document.createDocumentFragment();
    exactCells.forEach(function (cell) {
      var h = (cell.getAttribute('data-augment-handle') || '').toLowerCase();
      if (!h || native[h]) return;
      if (grid.querySelector('[data-augment-handle="' + cssEsc(h) + '"]')) return;
      frag.appendChild(document.importNode(cell, true));
    });
    grid.appendChild(frag);

    var noResults = document.getElementById('SearchNoResults');
    if (noResults) noResults.style.display = 'none';
    document.querySelectorAll('.template-search--empty').forEach(function (el) {
      el.classList.remove('template-search--empty');
    });

    var searchRoot = grid.closest('.template-search');
    if (searchRoot) {
      searchRoot.querySelectorAll('#FacetFiltersContainer, .collection__toolbar, .pagination-wrapper').forEach(function (el) {
        el.style.display = 'none';
      });
    }
  }

  function isExactShadeCell(cell) {
    return !!cell.querySelector('[data-search-variant-match="exact"]');
  }

  function finish(gridWrap) {
    gridWrap.setAttribute('data-search-augment-ready', '');
    gridWrap.removeAttribute('aria-busy');
  }

  function cssEsc(s) {
    if (window.CSS && CSS.escape) return CSS.escape(s);
    return String(s).replace(/["\\\]]/g, '\\$&');
  }

  if (document.readyState !== 'loading') augment();
  else document.addEventListener('DOMContentLoaded', augment);
  // facets.js dispatches this after every AJAX filter/sort grid replacement.
  document.addEventListener('collection:rerendered', function () {
    requestAnimationFrame(augment);
  });
})();
